// src/shared/constants/tableOrder.js

// Intergovernmental section display order (top-to-bottom)
export const INTERGOV_SECTION_ORDER = [
  "HousAuth In-Lieu Tax",
  "St Conn In-Lieu Taxes",
  "ST Educ Entitlements",
  "Admin Services",
  "Public Safety",
  "Human Services",
];

export const TOWN_SECTION_ORDER = [
  "General Government",
  "Community Development",
  "Administrative Services",
  "Public Safety",
  "Physical Services",
  "Sanitation",
  "Human Services",
  "Leisure & Culture",
];

export const DEBT_SECTION_ORDER = [
  "Debt Service",
  "Transfers Out",
];

// Map section name -> row order key (used for applying TABLE_ROW_ORDER
// to each subsection inside INTERGOVERNMENTAL)
export const SECTION_ORDER_KEY = {
  // Revenues – INTERGOVERNMENTAL subsections
  "intergovernmental:HousAuth In-Lieu Tax": "intergovernmental",
  "intergovernmental:St Conn In-Lieu Taxes": "stConnInLieuTaxes",
  "intergovernmental:ST Educ Entitlements": "stEducEntitlements",
  "intergovernmental:Admin Services": "adminServices",
  "intergovernmental:Public Safety": "publicSafety",
  "intergovernmental:Human Services": "humanServices",

  // Expenditures – TOWN subsections
  "town:General Government": "exp_generalGovernment",
  "town:Community Development": "exp_communityDevelopment",
  "town:Administrative Services": "exp_administrativeServices",
  "town:Public Safety": "exp_publicSafety",
  "town:Physical Services": "exp_physicalServices",
  "town:Human Services": "exp_humanServices",
  "town:Leisure & Culture": "exp_leisureAndCulture",

  // EXPENDITURES: Debt Service & Transfers
  "debtServiceAndTransfers:Transfers Out": "exp_transfersOut",
};

// Map of category -> desired row order (by display label)
export const TABLE_ROW_ORDER = {
  // Revenues
  taxes: [
    "Current Levy",
    "Auto Supplemental",
    "Delinquent Motor Vehicle Fees",
    "Prior Years",
    "Interest & Fees",
    "Miscellaneous Fees",
  ],

  stConnInLieuTaxes: [
    "St/CT Pilot",
    "Municpal Revenue Sharing",
    "Disability Exemption",
    "Veterans Exemption",
    "St/CT Telephone Access",
    "State Stabilization Grant",
    "Miscellaneous State Grant",
  ],

  stEducEntitlements: [
    "Magnet School Transportation",
    "ECS Cost Sharing Grant",
    "Spec Educ Excess/Agency",
    "Vocational Agriculture",
  ],

  licensesAndPermits: [
    "Building Inspection Fees",
    "Town Clerk Fees",
    "Physical Services Fees",
    "Refuse Permit Fees",
    "Health",
    "Fire Marshal",
  ],

  adminServices: [
    "ST/CT Motor Vehicle Grant",
    "ST/CT Historical Document Pres",
  ],

  publicSafety: [
    "Regional Dispatch Reimbursement",
    "Police Grants",
    "Civil Preparedness",
    "State Fire Service",
    "Volunteer Ambul Reimbursements",
  ],

  humanServices: [
    "Dial A Ride Grant",
    "Housing Auth Resident Services",
    "Youth & Family ST Grant",
    "NCAA Grant",
    "State Grants",
  ],

  chargesForServices: [
    "Planning & Zoning",
    "Town Clerk Recording Fees",
    "Town Clerk Conveyance Fee",
    "Educ/Community Serv Fees",
    "Solid Waste Tip Fees",
    "Sewer Inspection Fees",
    "Parks/Rec Swimming Fees",
    "Parks/Rec Program Fees",
    "Subdivision OT Inspection",
    "Fire Watch Services",
    "Health Soil Tests",
    "Senior Service Programs",
    "Senior Nutrition Program",
    "Library Fines",
    "Notary Services",
    "Passport Processing",
    "EV Charging Fees",
  ],

  otherRevenues: [
    "Probate Court Reimbursements",
    "Interest on Investments",
    "Lease Interest Income",
    "Lease Revenue",
    "Land Sales & Rentals",
    "Miscellaneous",
    "Educ Student Activities",
    "Educ Vo Ag Tuition",
    "Education Tuition/Other",
    "Purchasing - Auction Sales",
    "PhyServ Refunds & Sales",
    "Refuse Recycling",
    "Claims Reimbursements",
    "Public Safety Police",
    "Youth & Family Services",
    "Library Trustee Account",
    "Clinical Fees",
    "Library Miscellaneous",
    "Purchasing Card Rebates",
    "Attorney Fees Reimbursed",
    "Utilities Reimbursed",
  ],

  transfersIn: ["Capital Projects", "General Fund"],

  exp_generalGovernment: [
    "Town Council",
    "Town Manager",
    "Human Resources",
    "Facilities Maintenance",
  ],

  exp_communityDevelopment: [
    "Community Development",
    "Building Inspection",
    "Fire Marshal",
    "Health",
  ],

  exp_administrativeServices: [
    "Financial Administration",
    "Accounting",
    "Property Assessment",
    "Revenue Collection",
    "Town Clerk",
    "Voter Registration",
    "Legal Services",
    "Probate Services",
    "Insurance Pensions",
  ],

  exp_publicSafety: [
    "Police",
    "Volunteer Ambulance",
    "Fire",
    "Civil Preparedness",
  ],

  exp_physicalServices: ["Engineering", "Highway", "Fleet Maintenance"],

  exp_humanServices: [
    "Health Grants",
    "Youth/Family Services",
    "Senior & Community Services",
  ],

  exp_leisureAndCulture: [
    "Parks/Recreation",
    "Welles Turner Library",
    "South Glastonbury Library",
    "East Glastonbury Library",
  ],

  exp_transfersOut: [
    "Capital Reserve Fund",
    "Dog Fund",
    "OPEB Fund",
    "Sewer Operating",
    "Pension",
    "Contingency",
    "Capital Projects Fund",
  ],
};

/**
 * Apply a default display order to a list of rows for a given category.
 * - Uses TABLE_ROW_ORDER[categoryKey] to decide ordering by `row.label`
 * - Adds an `order` property (used by CommonTable's default sort)
 */
export function applyRowOrder(rows, categoryKey) {
  const orderList = TABLE_ROW_ORDER[categoryKey];

  if (!orderList) {
    // No explicit order defined; just return rows as-is
    return rows;
  }

  return rows.map((row, idx) => {
    const baseIndex = orderList.indexOf(row.label);
    return {
      ...row,
      order: baseIndex !== -1 ? baseIndex : orderList.length + idx, // unknown labels go after the known ones
    };
  });
}
